import {
  QButton,
  QCheckbox,
  QFormControl,
  QModal,
  QModalActions,
  QModalBody,
  QModalHeader,
  QRadio,
  QRadioGroup,
  QStack,
  QText,
  useFeatureFlag,
  useToastProvider,
} from '@qualio/ui-components';
import { CurrentUser } from '@qualio/ui-components/lib/types/CurrentUser';
import { ChangeEvent, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DocumentFormat,
  PageOrientation,
  QualioDocument,
} from '../../../../api/model/document';
import {
  isBasicUserInAccount,
  isQualityUserInAccount,
} from '../../../../util/CurrentUser';
import { isIssueRelated } from '../../DocumentOverview/RenderLogic';

interface DocumentExportProps {
  isOpen: boolean;
  onClose: () => void;
  document: QualioDocument;
  onExportDocument: (
    coverPageEnabled: boolean,
    controlledCopy: boolean,
    orientation: PageOrientation | undefined,
    contentShrinkingEnabled: boolean,
    onDocumentExportCompletion: () => void,
    onDocumentExportFailure: () => void,
    onAttachmentDownloadFailure: () => void,
  ) => void;
  onExport?: (
    coverPageEnabled: boolean,
    controlledCopy: boolean,
    contentShrinkingEnabled: boolean,
    includeChangeControl: boolean,
    includeDocx: boolean,
    orientation: PageOrientation | undefined,
  ) => void;
  currentUser: CurrentUser;
  activeExportMethod?: 'legacy' | 'fes' | 'word';
}

export const DocumentExportModal = ({
  isOpen,
  onClose,
  document,
  onExportDocument,
  onExport,
  currentUser,
  activeExportMethod,
}: DocumentExportProps) => {
  const { t } = useTranslation();
  const { showToast } = useToastProvider();
  const [controlledCopy, setControlledCopy] = useState(false);
  const defaultOrientation =
    document.orientation === 'landscape'
      ? PageOrientation.Landscape
      : PageOrientation.Portrait;
  const [orientation, setOrientation] =
    useState<PageOrientation>(defaultOrientation);
  const [coverPageEnabled, setCoverPageEnabled] = useState(true);
  const [contentShrinkingEnabled, setContentShrinkingEnabled] = useState(true);
  const [includeChangeControl, setIncludeChangeControl] = useState(false);
  const [includeDocx, setIncludeDocx] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const isQualityUser = isQualityUserInAccount(currentUser);
  const isBasicUser = isBasicUserInAccount(currentUser);
  const featureFlags = useFeatureFlag();

  const onControlledCopyChange = (value: string) => {
    const newValue = value === 'true';
    setControlledCopy(newValue);
  };

  const onCoverPageChange = (evt: ChangeEvent<HTMLInputElement>) => {
    setCoverPageEnabled(evt.target.checked);
  };

  const onContentShrinkingChange = (evt: ChangeEvent<HTMLInputElement>) => {
    setContentShrinkingEnabled(evt.target.checked);
  };
  const onIncludeChangeControlChange = (evt: ChangeEvent<HTMLInputElement>) => {
    setIncludeChangeControl(evt.target.checked);
  };
  const onIncludeDocxChange = (evt: ChangeEvent<HTMLInputElement>) => {
    setIncludeDocx(evt.target.checked);
  };

  const reset = useCallback(() => {
    setIsDownloading(false);
    setCoverPageEnabled(true);
    setIncludeChangeControl(false);
    setIncludeDocx(false);
  }, []);

  const onCloseCallback = useCallback(() => {
    reset();
    onClose();
  }, [onClose, reset]);

  const onDocumentExportFailure = (msg = t('notifications.exportFailed')) => {
    showToast({
      'data-cy': 'document-export-error-toast',
      status: 'error',
      title: t('notifications.error'),
      description: msg,
      id: 'export-document-error-toast',
    });
    setIsDownloading(false);
  };

  const onAttachmentDownloadFailure = () => {
    showToast({
      'data-cy': 'document-export-attachment-error-toast',
      status: 'error',
      title: t('notifications.error'),
      description: t('notifications.downloadingAttachmentsFailed'),
      id: 'export-attachment-error-toast',
    });
  };

  const handleExport = useCallback(() => {
    onExport?.(
      coverPageEnabled,
      controlledCopy,
      contentShrinkingEnabled,
      includeChangeControl,
      includeDocx,
      orientation,
    );
    onCloseCallback();
  }, [
    coverPageEnabled,
    controlledCopy,
    contentShrinkingEnabled,
    includeChangeControl,
    orientation,
    onExport,
    onCloseCallback,
    includeDocx,
  ]);

  const exportDocument = () => {
    setIsDownloading(true);
    onExportDocument(
      coverPageEnabled,
      controlledCopy,
      orientation,
      contentShrinkingEnabled,
      onCloseCallback,
      onDocumentExportFailure,
      onAttachmentDownloadFailure,
    );
  };

  const getExportHandler = () => {
    if (activeExportMethod === 'legacy') {
      return exportDocument;
    }
    if (activeExportMethod === 'fes') {
      return handleExport;
    }
    if (activeExportMethod === 'word') {
      return exportDocument;
    }
    return featureFlags['replace-export-ui'] ? handleExport : exportDocument;
  };

  const shouldShowOrientationOptions =
    document.document_format === DocumentFormat.QualioNativeDocument &&
    document.editor_version !== null;

  const shouldShowCoverPageOptions = document.editor_version !== null;

  const shouldShowTypeOptions = document.status_id === 'effective';

  const shouldShowShrinkingOption =
    document.document_format === DocumentFormat.DocumentUpload;

  const shouldShowIncludeChangeControlOption =
    activeExportMethod !== 'legacy' &&
    (activeExportMethod === 'fes' || featureFlags['replace-export-ui']) &&
    !isIssueRelated(document);

  const shouldShowIncludeDocxOption =
    !isBasicUser &&
    activeExportMethod !== 'legacy' &&
    (activeExportMethod === 'fes' || featureFlags['replace-export-ui']) &&
    document.editor_version !== null &&
    document.document_format === DocumentFormat.QualioNativeDocument;

  const controlledCopyValue = isQualityUser
    ? controlledCopy.toString()
    : 'false';

  return (
    <QModal onClose={onCloseCallback} isOpen={isOpen} size="xl">
      <QModalHeader>
        <QText>{t('modals.exportDocument')}</QText>
      </QModalHeader>
      <QModalBody>
        <QStack spacing={6}>
          <>
            <QText fontSize="md">
              {t('messages.qualioExportsDocumentsAsPdf')}
            </QText>
            {shouldShowTypeOptions && (
              <QFormControl label={t('tableHeaders.type')}>
                <QRadioGroup
                  value={controlledCopyValue}
                  onChange={onControlledCopyChange}
                  data-cy="export-modal-type-selection"
                >
                  <QStack direction="row">
                    <QRadio
                      value="false"
                      data-cy="export-modal-uncontrolled-type"
                    >
                      {t('labels.uncontrolled')}
                    </QRadio>
                    <QRadio
                      isDisabled={!isQualityUser}
                      value="true"
                      data-cy="export-modal-controlled-type"
                    >
                      {t('labels.controlled')}
                    </QRadio>
                  </QStack>
                </QRadioGroup>
              </QFormControl>
            )}
            {shouldShowOrientationOptions && (
              <QFormControl size="md" label={t('forms.orientation')}>
                <QRadioGroup
                  value={orientation}
                  onChange={(nextValue: string) =>
                    setOrientation(
                      nextValue === PageOrientation.Portrait
                        ? PageOrientation.Portrait
                        : PageOrientation.Landscape,
                    )
                  }
                  data-cy="export-modal-orientation-selection"
                >
                  <QStack direction="row">
                    <QRadio
                      value="portrait"
                      data-cy="export-modal-portrait-orientation"
                    >
                      {t('labels.portrait')}
                    </QRadio>
                    <QRadio
                      value="landscape"
                      data-cy="export-modal-landscape-orientation"
                    >
                      {t('labels.landscape')}
                    </QRadio>
                  </QStack>
                </QRadioGroup>
              </QFormControl>
            )}
            {shouldShowCoverPageOptions && (
              <QStack spacing={0}>
                <QFormControl
                  label={t('forms.coverPage')}
                  helper={t('forms.coverPageHelper')}
                >
                  <QCheckbox
                    isChecked={coverPageEnabled}
                    onChange={onCoverPageChange}
                    data-cy="export-modal-coverpage-selection"
                  >
                    {t('forms.includeCoverPage')}
                  </QCheckbox>
                </QFormControl>
              </QStack>
            )}
            {shouldShowIncludeChangeControlOption && (
              <QStack spacing={0}>
                <QFormControl
                  label={t('forms.includeChangeControl')}
                  helper={t('forms.includesChangeControlInformation')}
                >
                  <QCheckbox
                    isChecked={includeChangeControl}
                    onChange={onIncludeChangeControlChange}
                    data-cy="export-modal-include-change-control-selection"
                  >
                    {t('forms.includeChangeControl')}
                  </QCheckbox>
                </QFormControl>
              </QStack>
            )}
            {shouldShowShrinkingOption && (
              <QStack spacing={0}>
                <QFormControl
                  label={t('forms.contentShrinking')}
                  helper={t('forms.automaticallyShrinkContentForHeaders')}
                >
                  <QCheckbox
                    isChecked={contentShrinkingEnabled}
                    onChange={onContentShrinkingChange}
                    data-cy="export-modal-content-shrinking-selection"
                  >
                    {t('forms.enableContentShrinking')}
                  </QCheckbox>
                </QFormControl>
              </QStack>
            )}
            {shouldShowIncludeDocxOption && (
              <QStack spacing={0}>
                <QFormControl
                  label={t('forms.includeDocx')}
                  helper={t('forms.includesDocxInformation')}
                >
                  <QCheckbox
                    isChecked={includeDocx}
                    onChange={onIncludeDocxChange}
                    data-cy="export-modal-include-docx-selection"
                  >
                    {t('forms.includeDocx')}
                  </QCheckbox>
                </QFormControl>
              </QStack>
            )}
          </>
        </QStack>
      </QModalBody>
      <QModalActions>
        <QButton variant="outline" onClick={onCloseCallback}>
          {t('buttons.cancel')}
        </QButton>
        <QButton
          isLoading={isDownloading}
          onClick={getExportHandler()}
          data-cy="export-document-button"
        >
          {t('buttons.export')}
        </QButton>
      </QModalActions>
    </QModal>
  );
};
